import { IMachineryDraft, IMachineryIdentifyResult, IMachineryPlateResult } from '../interfaces/heavyDutyMachinery/IMachineryAssist';
import { IServiceRequestClarification } from '../interfaces/heavyDutyMachinery/IServiceRequest';
import { CustomSpecFieldDef } from '../constants/machineryCustomSpecFieldsByType';
import { apiService, apiUploadService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/**
 * How a call to the publishing assistant can end.
 *
 * Returned rather than thrown, same stance as the service assistant's own
 * AssistOutcome: not being switched on is an ordinary state of the platform,
 * and the screen answers it by putting the block away rather than showing
 * somebody a red message about something they did not do wrong.
 *
 * Three failures rather than that one's two, because there is a third here
 * that deserves its own sentence: `limit` is the host having used their
 * allowance for the day, which is neither broken nor missing, and telling them
 * "no disponible" for it would be a lie they cannot act on.
 */
export type MachineryAssistOutcome<T> =
  | { ok: true; value: T }
  | { ok: false; reason: 'unavailable' | 'limit' | 'error'; error?: string };

const SERVICE_UNAVAILABLE = 503;
const TOO_MANY_REQUESTS = 429;

/** Shared with the accessory assistant next door, which fails in exactly these three ways. */
export const assistFailureOf = (status?: number, error?: string): { ok: false; reason: 'unavailable' | 'limit' | 'error'; error?: string } => ({
  ok: false,
  reason: status === SERVICE_UNAVAILABLE ? 'unavailable' : status === TOO_MANY_REQUESTS ? 'limit' : 'error',
  error,
});

/**
 * Which machine this is, from a sentence somebody typed or dictated.
 *
 * An answer with no category is a real answer: "una máquina" identifies
 * nothing, and the wizard's own picker is still sitting right there.
 */
export const identifyMachinery = async (
  description: string,
  lang: string
): Promise<MachineryAssistOutcome<IMachineryIdentifyResult>> => {
  const response = await apiService<IMachineryIdentifyResult>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_MACHINERY_ASSIST_IDENTIFY,
    method: HTTP_METHODS.POST,
    body: { description, lang },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return { ok: true, value: { ...response.data, questions: response.data.questions ?? [] } };
  return assistFailureOf(response.status, response.error);
};

/**
 * The technical data sheet for that machine.
 *
 * `customSpecFields` is this client's own per-type schema (see
 * machineryCustomSpecFieldsByType) and it travels on purpose: the server has
 * no copy of it, so the only way it can ask for the right keys -- and throw
 * away every other one -- is for the screen that renders them to say which
 * they are. Sent as key/type/unit/options, never as anything the host typed.
 */
export const draftMachinery = async (
  description: string,
  categoryCode: string,
  lang: string,
  options: {
    manufacturerBrand?: string;
    modelName?: string;
    answers?: IServiceRequestClarification[];
    customSpecFields?: CustomSpecFieldDef[];
    /**
     * Turns one of this app's own translation keys into the host's language.
     * The schema stores keys, and the assistant needs words: without this it
     * would be told the label of `outreachM` is
     * "machinerySpecOutreachLabel", which says nothing to anybody.
     */
    translateKey?: (key: string) => string;
  } = {}
): Promise<MachineryAssistOutcome<IMachineryDraft>> => {
  const { manufacturerBrand, modelName, answers, customSpecFields, translateKey } = options;

  const response = await apiService<IMachineryDraft>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_MACHINERY_ASSIST_DRAFT,
    method: HTTP_METHODS.POST,
    body: {
      description,
      categoryCode,
      lang,
      manufacturerBrand,
      modelName,
      answers,
      customAttributes: (customSpecFields ?? []).map((field) => ({
        key: field.key,
        type: field.type,
        options: field.options?.map((option) => option.value),
        // The unit is part of the label in the form itself; here it is sent
        // separately so the answer arrives in metres rather than in whatever
        // the model happens to prefer.
        unit: field.unitKey && translateKey ? translateKey(field.unitKey) : undefined,
        label: translateKey ? translateKey(field.labelKey) : undefined,
      })),
    },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return { ok: true, value: { ...response.data, customAttributes: response.data.customAttributes ?? [] } };
  return assistFailureOf(response.status, response.error);
};

/**
 * What is written on a photo of the machine's identification plate.
 *
 * The chassis number is the field that most often gets typed wrong -- seventeen
 * characters of mixed letters and digits that somebody squints at -- and the
 * only one nobody can check afterwards by looking at the machine from across
 * the yard.
 *
 * The photo is sent and forgotten: it is not uploaded to storage, and the
 * server does not keep it either.
 */
export const readMachineryPlate = async (photo: File): Promise<MachineryAssistOutcome<IMachineryPlateResult>> => {
  const formData = new FormData();
  formData.append('photo', photo);

  const response = await apiUploadService<IMachineryPlateResult>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_MACHINERY_ASSIST_PLATE,
    formData,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return { ok: true, value: response.data };
  return assistFailureOf(response.status, response.error);
};
